import {
  buildCanonical,
  type SeoRouteConfig,
  type UmbrellaSeoRoutes,
} from '../seo/routes'
import {
  buildBreadcrumbSchema,
  buildFaqSchema,
  buildHowToSchema,
  buildSoftwareApplicationSchema,
} from '../seo/schema'
import type { ProcessingVariant } from '../ui/ProcessingBadge'

export type UtilityRouteStatus = 'available' | 'coming-soon'

export interface UtilityFaqItem {
  question: string
  answer: string
}

export interface UtilityToolDefinition<
  Key extends string = string,
  Category extends string = string,
> {
  key: Key
  path: string
  navLabel?: string
  analyticsKey: string
  category: Category
  title: string
  shortDescription: string
  longDescription: string
  icon: string
  status: UtilityRouteStatus
  processing: ProcessingVariant
  seo: SeoRouteConfig
  useCases: readonly string[]
  howTo: readonly string[]
  faq: readonly UtilityFaqItem[]
  relatedTools?: readonly Key[]
  schema?: {
    applicationCategory?: string
  }
}

export interface UtilityStaticRouteDefinition<Key extends string = string> {
  key: Key
  path: string
  navLabel?: string
  seo?: SeoRouteConfig
  includeInNav?: boolean
}

export interface UtilityAppRegistry<
  StaticKey extends string = string,
  Tool extends UtilityToolDefinition = UtilityToolDefinition,
> {
  domain: string
  staticRoutes: readonly UtilityStaticRouteDefinition<StaticKey>[]
  tools: readonly Tool[]
}

export type AvailableUtilityTool<Tool extends UtilityToolDefinition> =
  Tool & { status: 'available' }

export function availableUtilityTools<Tool extends UtilityToolDefinition>(
  tools: readonly Tool[],
): AvailableUtilityTool<Tool>[] {
  return tools.filter(
    (tool): tool is AvailableUtilityTool<Tool> => tool.status === 'available',
  )
}

export function buildUtilitySeoRoutes<
  StaticKey extends string,
  Tool extends UtilityToolDefinition,
>(
  registry: UtilityAppRegistry<StaticKey, Tool>,
): UmbrellaSeoRoutes<StaticKey | Tool['key']> {
  const staticRoutes = Object.fromEntries(
    registry.staticRoutes
      .filter((route): route is UtilityStaticRouteDefinition<StaticKey> & { seo: SeoRouteConfig } =>
        Boolean(route.seo),
      )
      .map((route) => [route.key, route.seo]),
  ) as Record<StaticKey, SeoRouteConfig>
  const toolRoutes = Object.fromEntries(
    availableUtilityTools(registry.tools).map((tool) => [
      tool.key,
      {
        ...tool.seo,
        jsonLd: {
          mode: 'required',
          schemas: buildToolJsonLdSchemas(registry.domain, tool),
        },
      } satisfies SeoRouteConfig,
    ]),
  ) as unknown as Record<Tool['key'], SeoRouteConfig>

  return {
    domain: registry.domain,
    routes: {
      ...staticRoutes,
      ...toolRoutes,
    } as Record<StaticKey | Tool['key'], SeoRouteConfig>,
  }
}

function buildToolJsonLdSchemas<Tool extends UtilityToolDefinition>(
  domain: string,
  tool: Tool,
): object[] {
  const canonical = buildCanonical(domain, tool.path)
  const schemas: object[] = [
    buildBreadcrumbSchema([
      { name: domain, url: buildCanonical(domain, '/') },
      { name: tool.title, url: canonical },
    ]),
    buildSoftwareApplicationSchema({
      name: tool.title,
      description: tool.shortDescription,
      category: tool.schema?.applicationCategory ?? 'UtilitiesApplication',
      url: canonical,
    }),
  ]

  if (tool.howTo.length > 0) {
    schemas.push(buildHowToSchema(tool.title, [...tool.howTo]))
  }

  if (tool.faq.length > 0) {
    schemas.push(buildFaqSchema(tool.faq.map(({ question, answer }) => ({ question, answer }))))
  }

  return schemas
}

export function buildUtilityRoutePathMap<
  StaticKey extends string,
  Tool extends UtilityToolDefinition,
>(
  registry: UtilityAppRegistry<StaticKey, Tool>,
): Record<StaticKey | Tool['key'], string> {
  return Object.fromEntries([
    ...registry.staticRoutes.map((route) => [route.key, route.path]),
    ...registry.tools.map((tool) => [tool.key, tool.path]),
  ]) as Record<StaticKey | Tool['key'], string>
}

export function relatedUtilityTools<Tool extends UtilityToolDefinition>(
  currentKey: Tool['key'],
  tools: readonly Tool[],
  limit = 3,
): Tool[] {
  const current = tools.find((tool) => tool.key === currentKey)
  if (!current) {
    throw new Error(`[utility registry] Unknown tool key: ${currentKey}`)
  }

  if (current.relatedTools && current.relatedTools.length > 0) {
    const byKey = new Map(tools.map((tool) => [tool.key, tool]))
    return current.relatedTools
      .map((key) => byKey.get(key))
      .filter((tool): tool is Tool => Boolean(tool))
      .slice(0, limit)
  }

  return tools
    .filter((tool) => tool.key !== currentKey && tool.category === current.category)
    .concat(tools.filter((tool) => tool.key !== currentKey && tool.category !== current.category))
    .slice(0, limit)
}

export interface ResolvedUtilityRoute<Key extends string = string> {
  key: Key
  slug?: string
}

export interface DynamicUtilityRoute<Key extends string = string> {
  key: Key
  prefix: string
  slugPrefix?: string
}

export function createUtilityRouteResolver<Key extends string>(config: {
  routes: Record<Key, string>
  fallbackKey: Key
  dynamicRoutes?: readonly DynamicUtilityRoute<Key>[]
}): (pathname?: string) => ResolvedUtilityRoute<Key> {
  const pathToKey = new Map(
    Object.entries(config.routes).map(([key, path]) => [path, key as Key]),
  )

  return (pathname = currentPathname()): ResolvedUtilityRoute<Key> => {
    const staticKey = pathToKey.get(pathname)
    if (staticKey) {
      return { key: staticKey }
    }

    for (const route of config.dynamicRoutes ?? []) {
      const slugPrefix = route.slugPrefix ?? `${route.prefix}/`
      if (pathname.startsWith(slugPrefix)) {
        return {
          key: route.key,
          slug: safeDecodeSlug(pathname.slice(slugPrefix.length)),
        }
      }
    }

    return { key: config.fallbackKey }
  }
}

function currentPathname(): string {
  if (typeof window === 'undefined') return '/'
  return window.location.pathname
}

function safeDecodeSlug(value: string): string {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}
